import React, { useState, useEffect } from 'react'
import { FormattedDate } from './../components/FormattedDate'
import { authFetch } from '../api/authFetch'
import SortedTableHeader from './../components/SortedTableHeader'
import InlineAlert from './../components/InlineAlert'

export default function Jobs() {
  const [jobs, setJobs] = useState([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [titleSearch, setTitleSearch] = useState('')
  const [search, setSearch] = useState('')
  const [locationSearch, setLocationSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [workModeFilter, setWorkModeFilter] = useState('')
  const [sort, setSort] = useState({ field: 'created_at', direction: 'DESC' })
  const [expandedJobId, setExpandedJobId] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [deleteError, setDeleteError] = useState(null)

  useEffect(() => {
    fetchJobs()
  }, [page, statusFilter, sort])

  const fetchJobs = async () => {
    setLoading(true)
    setLoadError(null)
    try {
      const params = new URLSearchParams({ page, limit: 20 })
      if (statusFilter) params.append('status', statusFilter)
      if (workModeFilter) params.append('workMode', workModeFilter)
      if (titleSearch) params.append('title', titleSearch)
      if (search) params.append('search', search)
      if (locationSearch) params.append('location', locationSearch)
      params.append('sortField', sort.field)
      params.append('sortDirection', sort.direction)

      const res = await authFetch(`/api/jobs?${params}`)
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}))
        throw new Error(errBody.detail || errBody.error || res.statusText)
      }
      const data = await res.json()
      setJobs(data.jobs || [])
      setTotalPages(data.totalPages || 1)
    } catch (err) {
      console.error('Failed to fetch jobs:', err)
      setLoadError(err.message || 'Failed to load jobs')
    } finally {
      setLoading(false)
    }
  }

  const handleSort = (field) => {
    setSort(prevSort => {
      if (prevSort.field === field) {
        // Same field: toggle sort direction
        return { field, direction: prevSort.direction === 'ASC' ? 'DESC' : 'ASC' }
      } else {
        // New field: default to descending sort
        return { field, direction: 'DESC' }
      }
    })
    setPage(1) // Reset to first page when sort changes
  }

  const handleSearch = (e) => {
    e.preventDefault()
    setPage(1)
    fetchJobs()
  }

  const handleDelete = async (jobId) => {
    const ok = window.confirm('Delete this job?')
    if (!ok) return

    setDeleteError(null)
    try {
      const res = await authFetch(`/api/jobs/${jobId}`, {
        method: 'DELETE'
      })

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}))
        throw new Error(errBody.error || errBody.detail || 'Failed to delete job')
      }

      setJobs(prev => prev.filter(j => j.id !== jobId))

      if (expandedJobId === jobId) {
        setExpandedJobId(null)
      }
    } catch (err) {
      console.error(err)
      setDeleteError(err.message || 'Failed to delete job')
    }
  }


  const statusBadge = (status) => {
    const colors = {
      done: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      error: 'bg-red-100 text-red-800'
    }
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    )
  }

  const columns = [
    { field: 'logo', label: 'Logo' },
    { field: 'company', label: 'Company' },
    { field: 'title', label: 'Title' },
    { field: 'job_id', label: 'Job ID' },
    { field: 'city', label: 'Location' },
    { field: 'work_mode', label: 'Work Mode' },
    { field: 'created_at', label: 'Created At' },
    { field: 'updated_at', label: 'Updated At' },
    { field: 'status', label: 'Status' }
  ]

  const companyLogoSrc = (job) => job.logo || '/images/company_default.svg'

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Jobs</h1>

      {loadError && (
        <InlineAlert variant="error" onDismiss={() => setLoadError(null)}>
          <p>{loadError}</p>
        </InlineAlert>
      )}
      {deleteError && (
        <InlineAlert variant="error" onDismiss={() => setDeleteError(null)}>
          <p>{deleteError}</p>
        </InlineAlert>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <form onSubmit={handleSearch} className="flex gap-4 flex-wrap items-end">
          <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
            <label htmlFor="jobs-filter-title" className="text-xs font-medium text-gray-600">
              Title
            </label>
            <input
              id="jobs-filter-title"
              type="text"
              value={titleSearch}
              onChange={(e) => setTitleSearch(e.target.value)}
              placeholder="Match job title…"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
          <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
            <label htmlFor="jobs-filter-search" className="text-xs font-medium text-gray-600">
              Search Jobs
            </label>
            <input
              id="jobs-filter-search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Title, company, description…"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
          <div className="flex flex-col gap-1 flex-1 min-w-[240px]">
            <label htmlFor="jobs-filter-location" className="text-xs font-medium text-gray-600">
              Location (state, city)
            </label>
            <input
              id="jobs-filter-location"
              type="text"
              value={locationSearch}
              onChange={(e) => setLocationSearch(e.target.value)}
              title="On-site/hybrid: must match. Remote: any location. All work types: remote rows included plus on-site/hybrid matching location."
              placeholder={'WA|Seattle|Bellevue'}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-mono text-sm"
            />
          </div>
          {/* Work mode filter */}
          <select
            value={workModeFilter}
            onChange={(e) => { setWorkModeFilter(e.target.value); setPage(1) }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">All work types</option>
            <option value="onsite">On-site</option>
            <option value="remote">Remote</option>
            <option value="hybrid">Hybrid</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">All statuses</option>
            <option value="done">Done</option>
            <option value="pending,processing">Pending</option>
            <option value="error,not_found,closed">Other</option>
          </select>
          <button type="submit" className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition">
            Search
          </button>
        </form>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                  {columns.map(col => (
                      <SortedTableHeader
                          key={col.field}
                          field={col.field}
                          label={col.label}
                          sort={sort}
                          onSort={handleSort}
                      />
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
              {jobs.map((job) => (
                  <React.Fragment key={job.id}>
                    <tr>
                      <td className="px-6 py-4 text-right">

                        <button
                            onClick={() =>
                                setExpandedJobId(expandedJobId === job.id ? null : job.id)
                            }
                            className="cursor-pointer"
                            title="View job description"
                        >
                          🔽
                        </button>
                        &nbsp;
                        <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDelete(job.id)
                            }}
                            className="text-red-600 hover:text-red-800 transition cursor-pointer"
                            title="Delete job"
                        >
                          ❌
                        </button>
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <img
                          src={companyLogoSrc(job)}
                          alt=""
                          className="h-6 w-6 object-contain rounded"
                          loading="lazy"
                          referrerPolicy="no-referrer"
                        />
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">{job.company}</td>
                      <td className="px-6 py-4 text-sm max-w-xs truncate">
                        <a
                            className="hover:underline text-indigo-700 hover:text-indigo-900"
                            onClick={(e) => e.stopPropagation()}
                            href={`https://www.linkedin.com/jobs/view/${job.job_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                          {job.title}
                        </a>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{job.job_id}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{[job.city, job.state].filter(Boolean).join(', ') || 'United States'}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{job.work_mode}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <FormattedDate isoDate={job.created_at} />
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <FormattedDate isoDate={job.updated_at} />
                      </td>
                      <td className="px-6 py-4">{statusBadge(job.status)}</td>
                    </tr>

                    {expandedJobId === job.id && (
                        <tr className="bg-gray-50">
                          <td colSpan={columns.length + 2} className="px-6 py-4 text-sm text-gray-700">
                            <div className="max-w-4xl space-y-2 transition-all duration-200 ease-in-out">
                              <p className="whitespace-pre-line">
                                {job.description || 'No description available'}
                              </p>

                              <a
                                  href={`https://www.linkedin.com/jobs/view/${job.job_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-block text-indigo-600 hover:underline text-sm"
                              >
                                Open on LinkedIn →
                              </a>
                            </div>
                          </td>
                        </tr>
                    )}
                  </React.Fragment>
              ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <p className="text-sm text-gray-500">Page {page} of {totalPages}</p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
